import { api } from '../../services/authService';
import { sanitizeVendorText } from '../../utils/signalProviders';
import {
  columnLabel,
  emptyCondition,
  emptyGroup,
  mergeConditionsInTree,
  operatorsForColumn,
} from './criteriaUtils';
import { nodeFromValueRule } from './offerValueRules';

function normalizeKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function hitKeys(hit) {
  return [
    hit?.column,
    hit?.column_name,
    hit?.display_name,
  ]
    .map(normalizeKey)
    .filter(Boolean);
}

export function pickColumnHit(hits, feature, displayLabel) {
  const list = Array.isArray(hits) ? hits : [];
  const wanted = new Set(
    [feature, displayLabel].map(normalizeKey).filter(Boolean)
  );
  if (!wanted.size) return null;
  return (
    list.find((hit) => hitKeys(hit).some((key) => wanted.has(key))) || null
  );
}

/** Parse values like "8-9", "8 – 9", "35-44" into inclusive numeric bounds. */
export function parseNumericRange(value) {
  const match = String(value || '')
    .trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*[-–—]\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const lowNum = Number(match[1]);
  const highNum = Number(match[2]);
  if (!Number.isFinite(lowNum) || !Number.isFinite(highNum) || lowNum === highNum) {
    return null;
  }
  if (lowNum < highNum) {
    return { low: match[1], high: match[2] };
  }
  return { low: match[2], high: match[1] };
}

/** Parse open-ended values like "3+" or "10 +" into the inclusive minimum ("3"). */
export function parseOpenEndedMinimum(value) {
  const match = String(value || '')
    .trim()
    .match(/^(-?\d+(?:\.\d+)?)\s*\+$/);
  if (!match || !Number.isFinite(Number(match[1]))) return null;
  return match[1];
}

/** Parse values like "<0.5" or "< 0.5" into the exclusive upper bound ("0.5"). */
export function parseLessThan(value) {
  const match = String(value || '')
    .trim()
    .match(/^<\s*(-?\d+(?:\.\d+)?)$/);
  if (!match || !Number.isFinite(Number(match[1]))) return null;
  return match[1];
}

const COMPARE_OPS = new Set(['gte', 'lte', 'gt', 'lt', 'between']);

export function conditionFromColumnHit(hit, value, operator = 'eq', value2 = '') {
  const blank = emptyCondition();
  const isCompare = COMPARE_OPS.has(operator);
  // A value list restricts the operator set, so comparisons have to drop it. Columns typed as
  // text in the catalog still need a numeric type for `>=` / `<=` / `<` / `>` / `BETWEEN`.
  let columnType = hit.column_type || '';
  if (isCompare && !operatorsForColumn(columnType, false, hit).includes(operator)) {
    columnType = /\./.test(`${value ?? ''}${value2 ?? ''}`) ? 'double' : 'int';
  }
  return {
    ...blank,
    catalog: hit.catalog || '',
    schema: hit.schema || '',
    table: hit.table || '',
    column: hit.column || '',
    column_type: columnType,
    has_value_list: isCompare ? false : Boolean(hit.has_value_list),
    operator: operator || 'eq',
    value: String(value ?? ''),
    value2: String(value2 ?? ''),
    folder_list: Array.isArray(hit.folder_list) ? hit.folder_list.filter(Boolean) : [],
    display_name: columnLabel(hit),
  };
}

async function resolveColumnForFeature(feature, displayLabel) {
  const queries = [...new Set(
    [feature, displayLabel].map((v) => String(v || '').trim()).filter(Boolean)
  )];
  for (const q of queries) {
    const res = await api.get('/api/v2/segments/column-typeahead', {
      params: { q, size: 12, visible: true },
    });
    const hits = Array.isArray(res.data?.hits) ? res.data.hits : [];
    const hit = pickColumnHit(hits, feature, displayLabel);
    if (hit) return hit;
  }
  return null;
}

function canCompare(hit, ...wanted) {
  const ops = operatorsForColumn(hit.column_type, false, hit);
  return wanted.every((op) => ops.includes(op));
}

export const GENERAL_TEMPLATE_VALUE = '__general__';

async function fetchTemplateFeatures(templateKey, limit) {
  if (templateKey === GENERAL_TEMPLATE_VALUE) {
    const res = await api.get('/api/v2/general_features', {
      params: { sort_order: 'asc', limit },
    });
    return Array.isArray(res.data) ? res.data : [];
  }
  const res = await api.get('/api/v2/vertical_features', {
    params: {
      vertical: templateKey,
      sort_order: 'asc',
      limit,
    },
  });
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * Map attribute/value rows to an AND criteria group.
 * Numeric ranges like "8-9" become a single `between` condition (two >= / <= conditions
 * only when the column does not offer `between`),
 * open-ended values like "3+" become a single >= condition, and
 * values like "<0.5" become a single < condition.
 * Features that cannot be mapped to a catalog column or have no value are skipped.
 * Rows landing on the same column are restated as one `is any of`, since a ranking can
 * hand us two values of one attribute and no profile has both.
 */
export async function buildCriteriaFromAttributeRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const children = [];
  const skipped = [];
  let applied = 0;

  for (const row of list) {
    const feature = String(row?.feature || '').trim();
    const displayLabel = String(row?.display_label || '').trim();
    const value = String(row?.top_conv_value ?? row?.value ?? '').trim();
    const label = sanitizeVendorText(displayLabel || feature || 'attribute');
    if (!feature) {
      skipped.push(label);
      continue;
    }
    if (!value) {
      skipped.push(label);
      continue;
    }
    const mapped = nodeFromValueRule(row);
    if (mapped) {
      children.push(mapped);
      applied += 1;
      continue;
    }
    const hit = await resolveColumnForFeature(feature, displayLabel);
    if (!hit?.catalog || !hit?.schema || !hit?.table || !hit?.column) {
      skipped.push(label);
      continue;
    }
    const range = parseNumericRange(value);
    if (range && canCompare(hit, 'between')) {
      children.push(conditionFromColumnHit(hit, range.low, 'between', range.high));
      applied += 1;
      continue;
    }
    if (range && canCompare(hit, 'gte', 'lte')) {
      children.push(conditionFromColumnHit(hit, range.low, 'gte'));
      children.push(conditionFromColumnHit(hit, range.high, 'lte'));
      applied += 1;
      continue;
    }
    const minimum = parseOpenEndedMinimum(value);
    if (minimum && canCompare(hit, 'gte')) {
      children.push(conditionFromColumnHit(hit, minimum, 'gte'));
      applied += 1;
      continue;
    }
    const lessThan = parseLessThan(value);
    if (lessThan && canCompare(hit, 'lt')) {
      children.push(conditionFromColumnHit(hit, lessThan, 'lt'));
      applied += 1;
      continue;
    }
    const ops = operatorsForColumn(hit.column_type, hit.has_value_list, hit);
    if (!ops.includes('eq')) {
      skipped.push(label);
      continue;
    }
    children.push(conditionFromColumnHit(hit, value));
    applied += 1;
  }

  return {
    criteria: mergeConditionsInTree({ ...emptyGroup(), children }),
    applied,
    skipped,
    total: list.length,
  };
}

/**
 * Fetch top attributes for a vertical or the General template and build an AND criteria group.
 */
export async function buildCriteriaFromVertical(templateKey, { limit = 10 } = {}) {
  const features = await fetchTemplateFeatures(templateKey, limit);
  return buildCriteriaFromAttributeRows(features);
}

function offerDetailsMode(offer) {
  if (!offer) return null;
  if (offer.has_metrics) return 'metrics';
  if (offer.has_values) return 'values';
  if (offer.has_vertical) return 'vertical';
  return 'general';
}

async function fetchOfferReportRow(organizationId, offerId) {
  const res = await api.get('/api/v2/offer_reports/', {
    params: {
      organization_id: organizationId,
      search: String(offerId),
    },
  });
  const rows = Array.isArray(res.data) ? res.data : [];
  return rows.find((row) => String(row.offer_id) === String(offerId)) || null;
}

async function fetchOfferTemplateRows(offer, organizationId, limit) {
  const mode = offerDetailsMode(offer);
  const id = offer.offer_id;
  const params = { organization_id: organizationId, sort_order: 'asc', limit };
  if (mode === 'metrics') {
    const [pairsRes, featuresRes] = await Promise.all([
      api.get(`/api/v2/offer_reports/${id}/top_pairs`, {
        params: { organization_id: organizationId },
      }),
      api.get(`/api/v2/offer_reports/${id}/features`, { params }),
    ]);
    return {
      mode,
      positive: Array.isArray(pairsRes.data?.positive) ? pairsRes.data.positive : [],
      features: Array.isArray(featuresRes.data) ? featuresRes.data : [],
    };
  }
  const path =
    mode === 'values'
      ? `/api/v2/offer_reports/${id}/values_features`
      : mode === 'vertical'
        ? `/api/v2/offer_reports/${id}/vertical_features`
        : `/api/v2/offer_reports/${id}/general_features`;
  const res = await api.get(path, { params });
  return {
    mode,
    positive: [],
    features: Array.isArray(res.data) ? res.data : [],
  };
}

/**
 * Build an AND criteria group from an offer's report, matching Generate segment.
 */
export async function buildCriteriaFromOffer(offerId, { organizationId, limit = 10 } = {}) {
  if (!organizationId || offerId == null || offerId === '') {
    return { criteria: null, applied: 0, skipped: [], total: 0, offer: null, missing: true };
  }
  const offer = await fetchOfferReportRow(organizationId, offerId);
  if (!offer) {
    return { criteria: null, applied: 0, skipped: [], total: 0, offer: null, missing: true };
  }
  const { mode, positive, features } = await fetchOfferTemplateRows(
    offer,
    organizationId,
    limit
  );
  let result;
  if (mode === 'metrics' && positive.length) {
    result = await buildCriteriaFromAttributeRows(positive);
  } else if (mode === 'vertical' && offer.category) {
    result = await buildCriteriaFromVertical(offer.category);
  } else if (mode === 'general') {
    result = await buildCriteriaFromVertical(GENERAL_TEMPLATE_VALUE);
  } else if (features.length) {
    result = await buildCriteriaFromAttributeRows(features);
  } else {
    return { criteria: null, applied: 0, skipped: [], total: 0, offer, missing: false };
  }
  return { ...result, offer, missing: false };
}
